import { FormRequest } from "@/services/bedrock";

type SourceFields = Pick<FormRequest, "sourceCategory" | "sourceSystem" | "internalIdentifier"> &
  Partial<Pick<FormRequest, "attribution">>;

/** "via TotalExpert route", "via Form Route" or "via API"; null for links made in SocialCoach. */
function viaLabel(request: Pick<FormRequest, "sourceCategory" | "sourceSystem">): string | null {
  if (request.sourceCategory === "route") {
    return request.sourceSystem === "TE" ? "via TotalExpert route" : "via Form Route";
  }
  if (request.sourceCategory === "api") return "via API";
  return null;
}

/** The campaign a route tagged the link with, as the CRM filled it in. */
export function formRequestCampaign(request: Partial<Pick<FormRequest, "attribution">>): string | null {
  return request.attribution?.utm?.campaign?.trim() || null;
}

/**
 * The muted "where it came from" line under a form link or response, shared by the coach's Form
 * Links, the player's Sent Links and Form Responses so all three read the same. Null when there's
 * nothing to say.
 */
export function formRequestSourceLine(
  request: SourceFields,
  options: { includeIdentifier?: boolean } = {}
): string | null {
  const { includeIdentifier = true } = options;
  const campaign = formRequestCampaign(request);
  const parts = [
    viaLabel(request),
    campaign && `Campaign: ${campaign}`,
    includeIdentifier ? request.internalIdentifier : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

/** Where the link came from, for the response's details. Links from before Form Routes have no category. */
export function formRequestOrigin(request: Pick<FormRequest, "sourceCategory" | "sourceSystem">): string {
  if (request.sourceCategory === "route") {
    return request.sourceSystem === "TE" ? "A TotalExpert route" : "A Form Route";
  }
  if (request.sourceCategory === "api") return "The API";
  return "Created in SocialCoach";
}

/** How a route found the player, from `attribution.route.player`. */
export function routePlayerLabel(resolution: string | undefined, playerName: string | null): string | null {
  switch (resolution) {
    case "matched":
      return playerName ? `${playerName}, matched from the link` : "Matched from the link";
    case "from_fan":
      return playerName ? `${playerName}, taken from the Fan's record` : "Taken from the Fan's record";
    case "not_found":
      return "No player matched the link, so it arrived with no player";
    case "ineligible":
      return "The player in the link can't use this form, so it arrived with no player";
    case "absent":
      return "The link had no player, so it arrived with no player";
    default:
      return null;
  }
}

/** How a route found the client's Fan, from `attribution.route.fan`. */
export function routeFanLabel(resolution: string | undefined): string | null {
  switch (resolution) {
    case "matched_fan":
      return "Recognized by SocialCoach Fan ID";
    case "matched_client_id":
    case "matched_internal_identifier":
      return "Recognized by Client ID";
    case "matched_email":
      return "Recognized by email";
    case "created":
      return "New Fan, created when they clicked";
    case "none":
      return "The link had no client details";
    default:
      return null;
  }
}
